import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '../shared/FormField';
import { NativeSelect } from '../shared/NativeSelect';
import { NATIVE_FIELD_CLASS } from '@/contabilidad/constants/ui';
import { NumberInput } from '@/components/ui/NumberInput';
import { cn } from '@/lib/utils';
import type { CuentaBancaria, CreateCajaPayload } from '../../types';

interface Props {
  open: boolean;
  caja?: CuentaBancaria | null;
  isSubmitting?: boolean;
  onSubmit: (payload: CreateCajaPayload) => Promise<void>;
  onClose: () => void;
}

const DEFAULTS: CreateCajaPayload = {
  nombre: '',
  tipo: 'banco',
  moneda: 'ARS',
  saldoInicial: 0,
};

export function CajaEditorModal({ open, caja, isSubmitting = false, onSubmit, onClose }: Props) {
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<'banco' | 'efectivo'>('banco');
  const [moneda, setMoneda] = useState<'ARS' | 'USD'>('ARS');
  const [saldoInicial, setSaldoInicial] = useState('0');
  const [errors, setErrors] = useState<{ nombre?: boolean }>({});

  const isEditing = !!caja;

  useEffect(() => {
    if (!open) return;
    if (caja) {
      setNombre(caja.nombre);
      setTipo(caja.tipo);
      setMoneda(caja.moneda);
      setSaldoInicial(String(caja.saldoInicial ?? 0));
    } else {
      setNombre(DEFAULTS.nombre);
      setTipo(DEFAULTS.tipo);
      setMoneda(DEFAULTS.moneda);
      setSaldoInicial('0');
    }
    setErrors({});
  }, [open, caja]);

  async function handleSubmit() {
    const newErrors = { nombre: !nombre.trim() };
    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }
    await onSubmit({
      nombre: nombre.trim(),
      tipo,
      moneda,
      saldoInicial: Number(saldoInicial) || 0,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">
            {isEditing ? 'Editar Caja' : 'Nueva Caja'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <FormField label="Nombre" required>
            <input
              type="text"
              placeholder="Ej: Galicia ARS, Efectivo USD..."
              value={nombre}
              onChange={(e) => { setNombre(e.target.value); setErrors((p) => ({ ...p, nombre: false })); }}
              className={cn(NATIVE_FIELD_CLASS, errors.nombre && 'border-rose-500 focus:border-rose-500')}
            />
            {errors.nombre && <p className="text-xs text-rose-500 mt-0.5">El nombre es obligatorio.</p>}
          </FormField>

          <div className="flex gap-3">
            <FormField label="Tipo" className="flex-1">
              <NativeSelect value={tipo} onChange={(e) => setTipo(e.target.value as 'banco' | 'efectivo')}>
                <option value="banco">Banco</option>
                <option value="efectivo">Efectivo</option>
              </NativeSelect>
            </FormField>

            <FormField label="Moneda" className="w-28">
              <NativeSelect
                value={moneda}
                onChange={(e) => setMoneda(e.target.value as 'ARS' | 'USD')}
                disabled={isEditing}
              >
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </NativeSelect>
            </FormField>
          </div>

          <FormField label="Saldo inicial (solo al crear)">
            <NumberInput
              id="saldo-inicial"
              decimals={2}
              min={0}
              value={saldoInicial}
              onChange={(value) => setSaldoInicial(value)}
              className={cn(NATIVE_FIELD_CLASS, 'text-right')}
              disabled={isEditing}
            />
          </FormField>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="rounded-full">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full">
            {isSubmitting ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Caja'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
